import React, { useCallback, useEffect, useMemo, useState } from "react";
import { aiApi } from "../../api/aiApi";
import { disputesApi, type UpdateDisputeRequest } from "../../api/disputesApi";
import { disputeLettersApi } from "../../api/disputeLettersApi";
import type { DisputeItem, DisputeLetter, LetterTemplate } from "../../types";
import {
  DISPUTE_ACCOUNT_TYPES,
  DISPUTE_STATUSES,
  FALLBACK_LETTER_TEMPLATES,
  LETTER_BUREAU_TARGETS,
  datetimeLocalValueToIso,
  isoToDatetimeLocalValue,
  modalCardClass,
  modalInputClass,
} from "./diyModalConstants";

export type DiyDisputeCard = DisputeItem & { clientName: string };

interface DiyDisputeDetailModalProps {
  card: DiyDisputeCard | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const DiyDisputeDetailModal: React.FC<DiyDisputeDetailModalProps> = ({ card, onClose, onSaved }) => {
  const [detailDraft, setDetailDraft] = useState<DisputeItem | null>(null);
  const [detailDisputeId, setDetailDisputeId] = useState<string | null>(null);
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplate[]>([]);
  const [detailLetters, setDetailLetters] = useState<DisputeLetter[]>([]);
  const [detailLettersLoading, setDetailLettersLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailSaveError, setDetailSaveError] = useState("");
  const [detailLetterError, setDetailLetterError] = useState("");
  const [generatingDetailLetter, setGeneratingDetailLetter] = useState(false);
  const [savingDetailLetter, setSavingDetailLetter] = useState(false);
  const [generatedLetterBody, setGeneratedLetterBody] = useState("");
  const [letterGenLetterType, setLetterGenLetterType] = useState("");
  const [letterGenBureau, setLetterGenBureau] = useState<string>(LETTER_BUREAU_TARGETS[0]);
  const [expandedHistoryLetterId, setExpandedHistoryLetterId] = useState<string | null>(null);
  const [generatingDisputeReason, setGeneratingDisputeReason] = useState(false);

  const disputeLetterOptions = useMemo(
    () => (letterTemplates.length > 0 ? letterTemplates : FALLBACK_LETTER_TEMPLATES),
    [letterTemplates]
  );

  useEffect(() => {
    if (!card) {
      setDetailDraft(null);
      setDetailDisputeId(null);
      return;
    }
    const options = disputeLetterOptions;
    const typeKey =
      options.find((t) => t.typeKey === card.letterType || t.name === card.letterType)?.typeKey ??
      (card.letterType || options[0]?.typeKey || "");
    setDetailDisputeId(card.id);
    setDetailDraft({ ...card, letterType: typeKey });
    setDetailSaveError("");
    setDetailLetterError("");
    setGeneratedLetterBody("");
    setLetterGenLetterType(typeKey || options[0]?.typeKey || "");
    setLetterGenBureau(LETTER_BUREAU_TARGETS[0]);
    setExpandedHistoryLetterId(null);
  }, [card, disputeLetterOptions]);

  useEffect(() => {
    if (!detailDisputeId) {
      return;
    }
    void disputeLettersApi.getTemplates().then(setLetterTemplates).catch(() => setLetterTemplates([]));
  }, [detailDisputeId]);

  useEffect(() => {
    if (!detailDisputeId) {
      setDetailLetters([]);
      setDetailLettersLoading(false);
      return;
    }
    let cancelled = false;
    setDetailLettersLoading(true);
    void disputeLettersApi
      .getLettersForDispute(detailDisputeId)
      .then((list) => {
        if (!cancelled) {
          setDetailLetters(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetailLetters([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLettersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailDisputeId]);

  useEffect(() => {
    if (!detailDisputeId) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailDisputeId, onClose]);

  const generateDisputeReasonWithAi = async () => {
    if (!detailDraft) {
      return;
    }
    const creditor = detailDraft.creditor.trim();
    if (!creditor || !detailDraft.accountType) {
      return;
    }
    setGeneratingDisputeReason(true);
    setDetailSaveError("");
    try {
      const balanceParsed = detailDraft.balance;
      const bureausParts: string[] = [];
      if (detailDraft.equifaxDisputed) {
        bureausParts.push("Equifax");
      }
      if (detailDraft.experianDisputed) {
        bureausParts.push("Experian");
      }
      if (detailDraft.transUnionDisputed) {
        bureausParts.push("TransUnion");
      }
      const bureaus = bureausParts.length > 0 ? bureausParts.join(", ") : null;
      const result = await aiApi.generateDisputeReason({
        creditorName: creditor,
        accountType: detailDraft.accountType,
        accountNumber: detailDraft.accountNumber.trim() || undefined,
        balance: Number.isNaN(balanceParsed) ? null : balanceParsed,
        status: detailDraft.status,
        bureaus,
        additionalContext: detailDraft.notes.trim() || null,
      });
      setDetailDraft((prev) => (prev ? { ...prev, disputeReason: result.reason } : null));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not generate dispute reason.";
      setDetailSaveError(message);
    } finally {
      setGeneratingDisputeReason(false);
    }
  };

  const submitDetailDisputeSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detailDraft || !card) {
      return;
    }
    const balanceNum = detailDraft.balance;
    if (Number.isNaN(balanceNum)) {
      setDetailSaveError("Enter a valid balance.");
      return;
    }
    setDetailSaving(true);
    setDetailSaveError("");
    try {
      const d = detailDraft;
      const now = new Date().toISOString();
      const payload: UpdateDisputeRequest = {
        creditor: d.creditor.trim(),
        accountType: d.accountType,
        accountNumber: d.accountNumber.trim(),
        balance: balanceNum,
        status: d.status,
        disputeReason: d.disputeReason.trim(),
        disputeStrategy: d.disputeStrategy,
        letterType: d.letterType.trim(),
        priorityRank: d.priorityRank,
        estimatedScoreImpact: d.estimatedScoreImpact,
        equifaxDisputed: d.equifaxDisputed,
        experianDisputed: d.experianDisputed,
        transUnionDisputed: d.transUnionDisputed,
        roundNumber: d.roundNumber,
        statuteOfLimitationsFlag: d.statuteOfLimitationsFlag,
        notes: d.notes.trim(),
        lastLetterSentAt: d.lastLetterSentAt ?? null,
        responseReceivedAt: d.responseReceivedAt ?? null,
        updatedAt: now,
      };
      const updated = await disputesApi.update(d.clientId, d.id, payload);
      setDetailDraft(updated);
      await onSaved();
    } catch {
      setDetailSaveError("Could not save dispute.");
    } finally {
      setDetailSaving(false);
    }
  };

  const handleGenerateDetailLetter = async () => {
    if (!detailDisputeId || !letterGenLetterType) {
      setDetailLetterError("Select a letter type.");
      return;
    }
    setDetailLetterError("");
    setGeneratingDetailLetter(true);
    try {
      const text = await disputeLettersApi.generateLetter(detailDisputeId, {
        letterType: letterGenLetterType,
        bureauTarget: letterGenBureau,
      });
      setGeneratedLetterBody(text);
    } catch {
      setDetailLetterError("Letter generation failed. Check AI configuration.");
    } finally {
      setGeneratingDetailLetter(false);
    }
  };

  const handleSaveDetailLetterSent = async () => {
    if (!detailDraft || !detailDisputeId) {
      return;
    }
    if (!generatedLetterBody.trim()) {
      setDetailLetterError("Generate or enter letter text before saving.");
      return;
    }
    const templateName =
      disputeLetterOptions.find((t) => t.typeKey === letterGenLetterType)?.name ?? letterGenLetterType;
    setDetailLetterError("");
    setSavingDetailLetter(true);
    try {
      await disputeLettersApi.saveLetter(detailDisputeId, {
        letterContent: generatedLetterBody,
        letterType: templateName,
        bureauTarget: letterGenBureau,
        markAsSent: true,
      });
      const list = await disputeLettersApi.getLettersForDispute(detailDisputeId);
      setDetailLetters(list);
      await onSaved();
      try {
        const fresh = await disputesApi.getById(detailDraft.clientId, detailDisputeId);
        setDetailDraft(fresh);
      } catch {
        /* ignore */
      }
    } catch {
      setDetailLetterError("Could not save letter.");
    } finally {
      setSavingDetailLetter(false);
    }
  };

  const handleBackdrop = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!detailDisputeId || !detailDraft || !card) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="diy-dispute-detail-title"
      onClick={handleBackdrop}
    >
      <div
        className={`${modalCardClass} max-h-[90vh] w-full max-w-2xl overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="diy-dispute-detail-title" className="text-lg font-semibold text-white">
          Dispute details
        </h3>
        <p className="mt-1 text-sm text-slate-400">
          {detailDraft.creditor} · {card.clientName}
        </p>

        <form className="mt-4 space-y-3" onSubmit={(e) => void submitDetailDisputeSave(e)}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-slate-500 sm:col-span-2">
              Creditor name
              <input
                className={modalInputClass}
                value={detailDraft.creditor}
                onChange={(e) =>
                  setDetailDraft((prev) => (prev ? { ...prev, creditor: e.target.value } : null))
                }
                autoComplete="organization"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Account type
              <select
                className={modalInputClass}
                value={detailDraft.accountType}
                onChange={(e) =>
                  setDetailDraft((prev) => (prev ? { ...prev, accountType: e.target.value } : null))
                }
              >
                {DISPUTE_ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-500">
              Account number
              <input
                className={modalInputClass}
                value={detailDraft.accountNumber}
                onChange={(e) =>
                  setDetailDraft((prev) => (prev ? { ...prev, accountNumber: e.target.value } : null))
                }
                autoComplete="off"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Balance
              <input
                type="number"
                step="0.01"
                className={modalInputClass}
                value={detailDraft.balance}
                onChange={(e) =>
                  setDetailDraft((prev) =>
                    prev ? { ...prev, balance: Number.parseFloat(e.target.value) || 0 } : null
                  )
                }
              />
            </label>
            <label className="block text-xs text-slate-500">
              Status
              <select
                className={modalInputClass}
                value={detailDraft.status}
                onChange={(e) =>
                  setDetailDraft((prev) => (prev ? { ...prev, status: e.target.value } : null))
                }
              >
                {DISPUTE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="block">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-slate-500">Dispute reason</span>
              <button
                type="button"
                onClick={() => void generateDisputeReasonWithAi()}
                disabled={generatingDisputeReason || !detailDraft.creditor.trim() || !detailDraft.accountType}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600/20 px-3 py-1.5 text-xs text-emerald-400 transition-colors hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {generatingDisputeReason ? "Generating…" : "Generate with AI"}
              </button>
            </div>
            <textarea
              className={`${modalInputClass} min-h-[100px] resize-y`}
              rows={4}
              value={detailDraft.disputeReason}
              onChange={(e) =>
                setDetailDraft((prev) => (prev ? { ...prev, disputeReason: e.target.value } : null))
              }
              placeholder="Describe why this item should be disputed…"
            />
          </div>

          <fieldset className="rounded-lg border border-slate-800 p-3">
            <legend className="px-1 text-xs text-slate-500">Bureaus</legend>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-200">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={detailDraft.equifaxDisputed}
                  onChange={(e) =>
                    setDetailDraft((prev) => (prev ? { ...prev, equifaxDisputed: e.target.checked } : null))
                  }
                  className="rounded border-slate-600"
                />
                Equifax
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={detailDraft.experianDisputed}
                  onChange={(e) =>
                    setDetailDraft((prev) => (prev ? { ...prev, experianDisputed: e.target.checked } : null))
                  }
                  className="rounded border-slate-600"
                />
                Experian
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={detailDraft.transUnionDisputed}
                  onChange={(e) =>
                    setDetailDraft((prev) => (prev ? { ...prev, transUnionDisputed: e.target.checked } : null))
                  }
                  className="rounded border-slate-600"
                />
                TransUnion
              </label>
            </div>
          </fieldset>

          <label className="block text-xs text-slate-500">
            Letter type
            <select
              className={modalInputClass}
              value={
                disputeLetterOptions.some((t) => t.typeKey === detailDraft.letterType)
                  ? detailDraft.letterType
                  : disputeLetterOptions[0]?.typeKey ?? ""
              }
              onChange={(e) =>
                setDetailDraft((prev) => (prev ? { ...prev, letterType: e.target.value } : null))
              }
            >
              {disputeLetterOptions.map((t) => (
                <option key={t.typeKey} value={t.typeKey}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs text-slate-500">
            Notes
            <textarea
              className={`${modalInputClass} min-h-[72px]`}
              value={detailDraft.notes}
              onChange={(e) =>
                setDetailDraft((prev) => (prev ? { ...prev, notes: e.target.value } : null))
              }
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-slate-500">
              Priority rank
              <input
                type="number"
                step="1"
                className={modalInputClass}
                value={detailDraft.priorityRank}
                onChange={(e) =>
                  setDetailDraft((prev) =>
                    prev ? { ...prev, priorityRank: Number.parseInt(e.target.value, 10) || 0 } : null
                  )
                }
              />
            </label>
            <label className="block text-xs text-slate-500">
              Round number
              <input
                type="number"
                step="1"
                min={1}
                className={modalInputClass}
                value={detailDraft.roundNumber}
                onChange={(e) =>
                  setDetailDraft((prev) =>
                    prev ? { ...prev, roundNumber: Math.max(1, Number.parseInt(e.target.value, 10) || 1) } : null
                  )
                }
              />
            </label>
            <label className="block text-xs text-slate-500">
              Last letter sent
              <input
                type="datetime-local"
                className={modalInputClass}
                value={isoToDatetimeLocalValue(detailDraft.lastLetterSentAt)}
                onChange={(e) =>
                  setDetailDraft((prev) =>
                    prev ? { ...prev, lastLetterSentAt: datetimeLocalValueToIso(e.target.value) } : null
                  )
                }
              />
            </label>
            <label className="block text-xs text-slate-500">
              Response received
              <input
                type="datetime-local"
                className={modalInputClass}
                value={isoToDatetimeLocalValue(detailDraft.responseReceivedAt)}
                onChange={(e) =>
                  setDetailDraft((prev) =>
                    prev ? { ...prev, responseReceivedAt: datetimeLocalValueToIso(e.target.value) } : null
                  )
                }
              />
            </label>
          </div>

          {detailSaveError && <p className="text-sm text-rose-300">{detailSaveError}</p>}

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
            <button
              type="button"
              className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={detailSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {detailSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>

        <div className="mt-6 border-t border-slate-800 pt-4">
          <h4 className="text-sm font-semibold text-white">Generate letter</h4>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-slate-500">
              Letter type
              <select
                className={modalInputClass}
                value={letterGenLetterType}
                onChange={(e) => setLetterGenLetterType(e.target.value)}
              >
                {disputeLetterOptions.map((t) => (
                  <option key={t.typeKey} value={t.typeKey}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-500">
              Bureau target
              <select
                className={modalInputClass}
                value={letterGenBureau}
                onChange={(e) => setLetterGenBureau(e.target.value)}
              >
                {LETTER_BUREAU_TARGETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            onClick={() => void handleGenerateDetailLetter()}
            disabled={generatingDetailLetter || !letterGenLetterType}
            className="mt-3 rounded-lg bg-emerald-600/20 px-4 py-2 text-sm font-medium text-emerald-400 hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {generatingDetailLetter ? "Generating…" : "Generate Letter"}
          </button>
          <label className="mt-3 block text-xs text-slate-500">
            Letter content
            <textarea
              className={`${modalInputClass} min-h-[160px] resize-y font-mono text-xs`}
              value={generatedLetterBody}
              onChange={(e) => setGeneratedLetterBody(e.target.value)}
              placeholder="Generate a letter or paste text here…"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleSaveDetailLetterSent()}
            disabled={savingDetailLetter || !generatedLetterBody.trim()}
            className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {savingDetailLetter ? "Saving…" : "Save & Mark as Sent"}
          </button>
          {detailLetterError && <p className="mt-2 text-sm text-rose-300">{detailLetterError}</p>}
        </div>

        <div className="mt-6 border-t border-slate-800 pt-4">
          <h4 className="text-sm font-semibold text-white">Letter history</h4>
          {detailLettersLoading ? (
            <p className="mt-2 text-sm text-slate-500">Loading letters…</p>
          ) : detailLetters.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">No letters saved for this dispute yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {detailLetters.map((letter) => (
                <li
                  key={letter.id}
                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-300"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-medium text-slate-200">{letter.letterType}</span>
                      <span className="mx-2 text-slate-600">·</span>
                      <span className="text-xs text-slate-500">
                        {new Date(letter.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedHistoryLetterId((prev) => (prev === letter.id ? null : letter.id))
                      }
                      className="rounded-md border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      {expandedHistoryLetterId === letter.id ? "Hide" : "View"}
                    </button>
                  </div>
                  {expandedHistoryLetterId === letter.id ? (
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-slate-800 bg-slate-950 p-2 text-xs text-slate-300">
                      {letter.letterContent}
                    </pre>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiyDisputeDetailModal;
